import { Fragment } from 'react';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { TransformedBooking } from '../../types';
import { CoverageSummary } from '@/features/job-scheduler/types/coverage';
import { ProviderMatchResult } from '@/features/users/providers/api/matches';

export type SuggestedProvidersCardProps = {
  booking?: TransformedBooking | null;
  summary?: CoverageSummary | null;
  matches?: ProviderMatchResult[];
  reason?: string;
  isLoading?: boolean;
  pinnedProviderIds: number[];
  onPin: (provider: ProviderMatchResult) => void;
  onUnpin: (providerId: number) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onAssign?: (provider: ProviderMatchResult) => void;
};

const placeholderMessage =
  'Select a booking in the scheduler to see matching cleaners for that postcode.';

export default function SuggestedProvidersCard({
  booking,
  summary,
  matches,
  reason,
  isLoading,
  pinnedProviderIds,
  onPin,
  onUnpin,
  isCollapsed = false,
  onToggleCollapse,
  onAssign,
}: SuggestedProvidersCardProps) {
  const noBookingSelected = !booking;
  type CoverageChipColor = 'success' | 'warning' | 'error';
  const coverageMeta: { label: string; color: CoverageChipColor } | null = summary
    ? {
        label:
          summary.coverage === 'direct'
            ? 'Coverage: strong'
            : summary.coverage === 'nearby'
            ? 'Coverage: nearby only'
            : 'Coverage: none',
        color:
          summary.coverage === 'direct'
            ? 'success'
            : summary.coverage === 'nearby'
            ? 'warning'
            : 'error',
      }
    : null;

  const hasMatches = Boolean(matches && matches.length);

  const renderAvailability = (provider: ProviderMatchResult) => {
    if (provider.availability.available_on_date) {
      return 'Available on the selected date';
    }

    if (provider.availability.reason === 'provider_unavailable') {
      return 'Unavailable • booked time off';
    }

    return 'Availability unknown';
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader
        title="Suggested providers"
        subheader={
          booking
            ? `${booking.address.postcode} • ${booking.serviceLabel}`
            : 'Coverage hints for each booking'
        }
        action={
          onToggleCollapse ? (
            <IconButton size="small" onClick={onToggleCollapse}>
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          ) : undefined
        }
      />
      <Divider />
      <Collapse in={!isCollapsed} unmountOnExit>
        <CardContent sx={{ p: 2.5 }}>
          {noBookingSelected ? (
            <Typography color="text.secondary" fontSize={14}>
              {placeholderMessage}
            </Typography>
          ) : isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography color="text.secondary" fontSize={14}>
              Fetching cleaner suggestions…
            </Typography>
          </Stack>
        ) : !hasMatches ? (
          <Typography color="text.secondary" fontSize={14}>
            {summary?.message || reason ||
              'No recommended cleaners for this postcode yet.'}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {coverageMeta && (
              <Chip
                label={coverageMeta.label}
                color={coverageMeta.color}
                size="small"
                sx={{ alignSelf: 'flex-start' }}
              />
            )}

            {matches?.map((provider, idx) => {
              const isPinned = pinnedProviderIds.includes(
                provider.provider_id
              );
              const previousBookingsCount =
                provider.assigned_bookings_at_address ?? 0;
              const hasPreviouslyCleaned = previousBookingsCount > 0;
              return (
                <Fragment key={provider.provider_id}>
                  {idx > 0 && <Divider flexItem />}
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                      {provider.name
                        .split(' ')
                        .map((part) => part[0])
                        .slice(0, 2)
                        .join('')}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={600}>{provider.name}</Typography>
                        <Chip
                          label={
                            provider.match_type === 'direct'
                              ? 'Direct coverage'
                              : 'Nearby only'
                          }
                          size="small"
                          color={
                            provider.match_type === 'direct'
                              ? 'success'
                              : 'warning'
                          }
                        />
                      </Stack>
                      <Typography fontSize={12} color="text.secondary">
                        {provider.match_type === 'direct'
                          ? 'Covers this postcode'
                          : 'Nearby coverage only'}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        Rating{' '}
                        {typeof provider.rating === 'number'
                          ? provider.rating.toFixed(1)
                          : 'N/A'}{' '}
                        • {provider.service_types.join(', ')}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {renderAvailability(provider)}
                      </Typography>
                      {hasPreviouslyCleaned && (
                        <Stack direction="row" spacing={1} mt={0.5}>
                          <Chip
                            label="Previous Cleaner"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{
                              fontSize: 11,
                              height: 22,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        </Stack>
                      )}
                      <Stack direction="row" spacing={1} mt={1}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!onAssign}
                          onClick={() => onAssign?.(provider)}
                        >
                          Assign
                        </Button>
                      </Stack>
                    </Box>
                    <IconButton
                      color={isPinned ? 'primary' : 'default'}
                      aria-label={isPinned ? 'Unpin provider' : 'Pin provider'}
                      onClick={() =>
                        isPinned
                          ? onUnpin(provider.provider_id)
                          : onPin(provider)
                      }
                      size="small"
                    >
                      {isPinned ? (
                        <BookmarkIcon fontSize="small" />
                      ) : (
                        <BookmarkBorderOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Stack>
                </Fragment>
              );
            })}
          </Stack>
            )}
          </CardContent>
      </Collapse>
    </Card>
  );
}
